import { Box } from '@mui/system';
import { AxiosResponse } from 'axios';
import React, { useCallback, useMemo } from 'react';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import {
  REVIEW_SENTIMENT_ENUM,
  SENTIMENT_ENUM_TO_STRING
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/constants';
import { ProductReviewDocument } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/types';
import SegmentNoDataPlaceholder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentNoDataPlaceholder';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductReviewCardLoadingV2 from 'src/components/BeaconV2Components/ProductReviewCard/ProductReviewCardLoadingV2';
import ProductReviewCardV2 from 'src/components/BeaconV2Components/ProductReviewCard/ProductReviewCardV2';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import AdvancedSearchParser from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/AdvancedSearchParser';
import { AdvancedSearchResponse } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/types';
import { useDebouncedValue, useMainTimePeriod } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import { ReviewDetailsPopup } from 'src/components/BeaconV2Components/ProductReviewCard/ReviewDetailsPopup';

interface UseProductReviewDataParams {
  searchTerm?: string;
  page?: number;
}

/**
 * Total number of items to display per page.
 */
const PAGE_SIZE = 8;

const useProductReviewData = ({
  searchTerm,
  page
}: UseProductReviewDataParams): { data: ProductReviewDocument[]; isLoading: boolean } => {
  const { startWeek, endWeek } = useMainTimePeriod();
  const requestBuilder = useBaseMetricRequestBuilder({
    startWeekId: startWeek,
    endWeekId: endWeek,
    requestId: 'productReviewGrid',
    fields: [],
    indexName: 'review-metrics'
  });

  requestBuilder
    .setPageSize(PAGE_SIZE)
    .setPageNumber(page)
    .clearSortFields()
    .setSearchBy('child')
    .modifyAggregation((aggBuilder) => {
      aggBuilder.setGroupByFieldName('');
      aggBuilder.setSortByAggregationFieldAsNull();
      return aggBuilder;
    })
    .setDoAggregation(false)
    .setReturnDocuments(true)
    .addSortFieldWithOnlyFieldName('reviewTime', 'desc')
    .addSortFieldWithOnlyFieldName('retailSales', 'desc');

  if (searchTerm) {
    requestBuilder.addNestedFilterCondition('should', [
      { fieldName: 'reviewText', values: [searchTerm] },
      { fieldName: 'reviewTitle', values: [searchTerm] }
    ]);
  }
  const request = [requestBuilder.build()];

  const { data: response, isLoading } = useGenericAdvancedSearch({
    requestId: 'productReviewGrid',
    requestBody: request,
    queryKeys: request
  });

  const data = useMemo(() => {
    if (response) {
      try {
        const parser = new AdvancedSearchParser(response as AxiosResponse<AdvancedSearchResponse[]>);
        return parser.getDocuments();
      } catch (error) {
        console.error('Failed to fetch review data', error);
        return null;
      }
    } else {
      return null;
    }
  }, [response]);

  return { data, isLoading };
};

export interface ReviewType {
  reviewTitle: string;
  product: ProductEntity;
  stacklineSku: string;
  reviewTime: number;
  reviewText: string;
  user: { username: string };
  reviewId: string;
  stars: number;
  reviewQualityScore: number;
  wordCountQualityScore: number;
  keywordCountQualityScore: number;
  writingQualityScore?: number;
  imageScore: number;
  videoScore: number;
  sentimentType: 'positive' | 'neutral' | 'negative';
}

export interface ReviewPopupState {
  open: boolean;
  review: ReviewType | null;
}

/**
 * Renders a searchable grid of product reviews with pagination.
 */
const ProductReviewGrid = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState('');
  const [reviewPopup, setReviewPopup] = React.useState<ReviewPopupState>({
    open: false,
    review: null
  });

  // Debounce the search term to prevent excessive API calls
  const debouncedSearchTerm = useDebouncedValue(searchInput, 500);

  // Fetch the product review data
  const { data, isLoading } = useProductReviewData({ searchTerm: debouncedSearchTerm, page: currentPage });

  const mainTimePeriod = useMainTimePeriod();

  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, mainTimePeriod]);

  const renderItems = useCallback(() => {
    switch (true) {
      case isLoading:
        return (
          <Box
            sx={{
              marginTop: '32px',
              display: 'grid',
              gridTemplateColumns: '0.5fr 0.5fr',
              gap: '16px'
            }}
          >
            {(Array.from({ length: PAGE_SIZE }) ?? []).map((_, index) => (
              <ProductReviewCardLoadingV2 key={index} />
            ))}
          </Box>
        );
      case !isLoading && data?.length > 0:
        return (
          <Box
            sx={{
              marginTop: '32px',
              display: 'grid',
              gridTemplateColumns: '0.5fr 0.5fr',
              gap: '16px'
            }}
          >
            {(data ?? []).map((item) => (
              <ProductReviewCardV2
                key={item.id}
                setReviewPopup={setReviewPopup}
                review={{
                  product: item.product,
                  reviewTitle: item.reviewTitle,
                  stacklineSku: item.stacklineSku,
                  reviewTime: item.reviewTime,
                  reviewText: item.reviewText,
                  user: { username: item.user.username },
                  reviewId: item.reviewId,
                  stars: item.stars,
                  reviewQualityScore: item.reviewQualityScore,
                  wordCountQualityScore: item.wordCountQualityScore,
                  keywordCountQualityScore: item.keywordCountQualityScore,
                  imageScore: item.imageScore,
                  videoScore: item.videoScore,
                  sentimentType: SENTIMENT_ENUM_TO_STRING[item.reviewSentiment ?? REVIEW_SENTIMENT_ENUM.NEUTRAL]
                }}
              />
            ))}
          </Box>
        );
      default:
        return (
          <Flex marginTop="64px" justifyContent="center">
            <SegmentNoDataPlaceholder
              title="Data currently unavailable"
              subtitle="There is currently no data matching your selected criteria. Please try another query to view insights."
              maxWidth={238}
            />
          </Flex>
        );
    }
  }, [data, isLoading]);

  return (
    <Flex flexDirection="column">
      <Box>
        <SearchTextField
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInput(e.target.value);
          }}
          placeholder="Search reviews"
        />
      </Box>

      {renderItems()}
      {data?.length === 0 ? null : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
          <Pagination
            shouldUseInfinitePagination
            currentPage={currentPage}
            onClickNext={() => {
              if (!(data?.length < PAGE_SIZE) && !isLoading) {
                setCurrentPage((page) => page + 1);
              }
            }}
            onClickPrevious={() => setCurrentPage((page) => Math.max(1, page - 1))}
          />
        </Box>
      )}
      {reviewPopup.review && (
        <ReviewDetailsPopup
          review={reviewPopup.review}
          open={reviewPopup.open}
          onClose={() => setReviewPopup(() => ({ open: false, review: null }))}
        />
      )}
    </Flex>
  );
};

export default ProductReviewGrid;
